import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface SourceMini { id: string; topic: string; language: string }

export default function SourceAnalyticsPanel({ sources }: { sources: SourceMini[] }) {
  const [rows, setRows] = useState<any[]>([]);

  useEffect(() => {
    const since = new Date();
    since.setDate(since.getDate() - 7);
    supabase
      .from("automation_logs")
      .select("source_id, status, items_fetched, items_stored, step, trigger_type, created_at")
      .eq("step", "run_summary")
      .gte("created_at", since.toISOString())
      .order("created_at", { ascending: false })
      .limit(2000)
      .then(({ data }) => setRows(data || []));
  }, []);

  const sourceMap = useMemo(() => Object.fromEntries(sources.map((s) => [s.id, s])), [sources]);

  const stats = useMemo(() => {
    const total = rows.length;
    const success = rows.filter((r) => r.status === "success").length;
    const failed = rows.filter((r) => r.status === "failed" || r.status === "error").length;
    const partial = rows.filter((r) => r.status === "partial").length;
    const totalFetched = rows.reduce((a, r) => a + (r.items_fetched || 0), 0);
    const totalStored = rows.reduce((a, r) => a + (r.items_stored || 0), 0);
    const successRate = total ? Math.round((success / total) * 100) : 0;
    const dupes = totalFetched - totalStored;

    const byLang: Record<string, number> = {};
    const bySource: Record<string, { runs: number; stored: number; failed: number }> = {};
    for (const r of rows) {
      const src = sourceMap[r.source_id];
      const lang = src?.language || "?";
      byLang[lang] = (byLang[lang] || 0) + (r.items_stored || 0);
      const key = src?.topic || r.source_id || "—";
      if (!bySource[key]) bySource[key] = { runs: 0, stored: 0, failed: 0 };
      bySource[key].runs++;
      bySource[key].stored += r.items_stored || 0;
      if (r.status === "failed" || r.status === "error") bySource[key].failed++;
    }
    const topActive = Object.entries(bySource).sort((a, b) => b[1].stored - a[1].stored).slice(0, 5);
    const topFailing = Object.entries(bySource).filter(([, v]) => v.failed > 0).sort((a, b) => b[1].failed - a[1].failed).slice(0, 5);

    return { total, success, failed, partial, totalFetched, totalStored, successRate, dupes, byLang, topActive, topFailing };
  }, [rows, sourceMap]);

  const StatCard = ({ label, value, sub }: { label: string; value: string | number; sub?: string }) => (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Exécutions (7j)" value={stats.total} />
        <StatCard label="Taux de succès" value={`${stats.successRate}%`} sub={`${stats.success} ok · ${stats.failed} ko · ${stats.partial} partiel`} />
        <StatCard label="Articles récupérés" value={stats.totalFetched} sub={`${stats.dupes} doublons filtrés`} />
        <StatCard label="Articles stockés" value={stats.totalStored} />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-sm">Répartition par langue</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {Object.keys(stats.byLang).length === 0 && <p className="text-xs text-muted-foreground">Aucune donnée</p>}
            {Object.entries(stats.byLang).sort((a, b) => b[1] - a[1]).map(([lang, count]) => {
              const max = Math.max(...Object.values(stats.byLang));
              const pct = max ? (count / max) * 100 : 0;
              return (
                <div key={lang}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium uppercase">{lang}</span>
                    <span className="text-muted-foreground">{count}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-sm">Sources les plus actives</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {stats.topActive.length === 0 && <p className="text-xs text-muted-foreground">Aucune donnée</p>}
            {stats.topActive.map(([name, v]) => (
              <div key={name} className="flex justify-between text-xs">
                <span className="truncate">{name}</span>
                <span className="text-muted-foreground">{v.stored} stockés / {v.runs} runs</span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-sm">Sources en échec</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {stats.topFailing.length === 0 && <p className="text-xs text-muted-foreground">Aucune erreur récente 🎉</p>}
            {stats.topFailing.map(([name, v]) => (
              <div key={name} className="flex justify-between text-xs">
                <span className="truncate">{name}</span>
                <span className="text-destructive">{v.failed} échecs</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
